'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowUpRight, ArrowRight } from '@phosphor-icons/react';
import { ArticleIcon } from '@/components/icons';
import type { BlogPostPreview } from '@/lib/api/blog';
import { blogArticleDate } from '@/lib/blog-date';

const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

// API serialises naive Moscow DATETIME as UTC, so format in Europe/Moscow: a post
// published at 01:00 MSK must not show the previous day. Fixed timezone and month
// names keep the server and browser text identical.
const MOSCOW_DATE = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Moscow',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

export function blogDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(
    MOSCOW_DATE.formatToParts(date).map((part) => [part.type, part.value])
  );
  return `${Number(parts.day)} ${MONTHS[Number(parts.month) - 1]} ${parts.year}`;
}

export function BlogMeta({ post }: { post: BlogPostPreview }) {
  const dateValue = blogArticleDate(post);
  const date = dateValue ? blogDate(dateValue) : '';
  return (
    <div className="journal-meta">
      {date && <time dateTime={dateValue || undefined}>{date}</time>}
      {post.reading_time > 0 && <span>{post.reading_time} мин чтения</span>}
    </div>
  );
}

export function BlogCover({
  post,
  priority = false,
  featured = false,
}: {
  post: BlogPostPreview;
  priority?: boolean;
  featured?: boolean;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return (
    <div className="journal-cover">
      {post.image_url && post.image_url !== failedUrl ? (
        <Image
          src={post.image_url}
          alt={post.title}
          fill
          priority={priority}
          sizes={
            featured
              ? '(max-width: 760px) calc(100vw - 32px), (max-width: 1380px) 56vw, 720px'
              : '(max-width: 600px) calc(100vw - 32px), (max-width: 960px) 46vw, 410px'
          }
          quality={85}
          onError={() => setFailedUrl(post.image_url)}
        />
      ) : (
        <div className="journal-cover-placeholder" aria-hidden="true">
          <ArticleIcon size={44} weight="light" />
        </div>
      )}
    </div>
  );
}

export function BlogCard({
  post,
  featured = false,
  heading = 'h2',
  priority = false,
}: {
  post: BlogPostPreview;
  featured?: boolean;
  heading?: 'h2' | 'h3';
  priority?: boolean;
}) {
  const Heading = heading;
  return (
    <article className={`journal-card${featured ? ' journal-card-featured' : ''}`}>
      <Link href={`/blog/${post.slug}`} className="journal-card-link">
        <BlogCover post={post} featured={featured} priority={priority} />
        <div className="journal-card-copy">
          <div className="journal-card-labels">
            <div className="journal-category">
              {post.tags
                .filter((tag) => tag !== 'Архив')
                .slice(0, 2)
                .join(' / ') || 'Азиаточка'}
            </div>
            {post.is_archive && <span className="journal-archive-label">Архив</span>}
          </div>
          <Heading>{post.title}</Heading>
          {post.excerpt && <p className="journal-excerpt">{post.excerpt}</p>}
          <div className="journal-card-bottom">
            <BlogMeta post={post} />
            <span className="journal-card-arrow" aria-hidden="true">
              {featured ? <ArrowRight size={22} /> : <ArrowUpRight size={20} />}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

export function BlogGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="journal-grid" aria-label="Загружаем статьи" role="status">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="journal-skeleton" aria-hidden="true">
          <div />
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}
